import { NextSeo } from 'next-seo';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useScreenSize } from '@repo/utils';

import { tw } from '../../utils';
import { Icon } from '../Icon';

export type PasswordScreenProps = {
  // Function to set the restricted state
  setIsRestricted: (isRestricted: boolean) => void;
  // Background image for desktop
  backgroundDesktop?: string;
  // Background image for mobile
  backgroundMobile?: string;
  // Text color
  textColor?: 'white' | 'black';
  // Container background color class
  containerBackgroundColorClass?: string;
  // Backgrround Image Base URL
  imageBaseUrl?: string;
};

interface PasswordInputs {
  code: string;
}

export const PasswordScreen: React.FC<PasswordScreenProps> = ({
  setIsRestricted,
  backgroundDesktop,
  backgroundMobile,
  textColor = 'black',
  containerBackgroundColorClass,
  imageBaseUrl,
}: PasswordScreenProps) => {
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isMobile } = useScreenSize();

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordInputs>({
    defaultValues: {
      code: '',
    },
  });

  const submitHandler = async (data: PasswordInputs) => {
    try {
      setLoading(true);
      const response = await fetch('/api/checkPassword', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: data.code }),
      });

      if (response.status === 200) {
        setIsRestricted(false);
      } else {
        setHasError(true);
      }
    } catch (error) {
      console.error('Error occurred updating username:', error);
    } finally {
      setLoading(false);
    }
  };

  const background = isMobile
    ? backgroundMobile || 'space-mobile'
    : backgroundDesktop || 'space-desktop';

  return (
    <>
      <NextSeo
        title='Restricted Access'
        openGraph={{
          title: 'Restricted Access',
        }}
      />

      <div
        style={{
          backgroundImage: `url(${
            imageBaseUrl ?? process.env.NEXT_PUBLIC_WEARABLES_CDN
          }/stoodio/backgrounds/${background}.png)`,
        }}
        className='max-h-screen-safe h-screen bg-cover bg-left bg-no-repeat'
      >
        <div className='flex h-full w-full items-center justify-center'>
          <div
            className={tw(
              'flex max-w-[400px] flex-col gap-4 rounded-[30px] border-2 border-black px-10 py-12 md:min-w-[400px]',
              textColor === 'black' ? 'text-black' : 'text-white',
              containerBackgroundColorClass
                ? containerBackgroundColorClass
                : 'bg-sunsetSherbert',
            )}
          >
            <div className='flex w-full gap-2'>
              <Icon
                name='space-doodles-caution'
                folder='core'
                height={45}
                width={45}
                className='-mt-1 min-h-[50px] min-w-[50px]'
                baseURL={imageBaseUrl ?? process.env.NEXT_PUBLIC_WEARABLES_CDN}
              />

              <div className='flex w-full flex-col gap-1'>
                <div
                  style={{
                    background:
                      'linear-gradient(to right, #FF8FD4, #FF8FD4 20%, #FFC196 20%, #FFC196 40%, #FFE871 40%, #FFE871 60%, #A5FFC3 60%, #A5FFC3 80%, #89E9FF 80%, #89E9FF 100%)',
                  }}
                  className='bg-bubblegum-300 h-3 w-full rounded-full border-2 border-black'
                ></div>

                <p className='font-fredoka text-center font-semibold tracking-wide'>
                  Restricted Data Portal
                </p>
              </div>
            </div>

            <p className='font-kumbh'>
              Authorization sequence required. Input your unique decryption key
              for access.
            </p>

            <form
              onSubmit={handleSubmitPassword(submitHandler)}
              className='flex flex-col gap-3'
            >
              <input
                id='code'
                {...registerPassword('code', {
                  required: 'Decryption Key Required',
                })}
                onKeyUp={() => hasError && setHasError(false)}
                disabled={loading}
                type='password'
                placeholder='Enter Decryption Key'
                className='h-10 w-full rounded-full border-2 border-black bg-white/50 px-4 focus:border-black focus:outline-none focus:ring-0'
              />

              {hasError ? (
                <div className='flex items-center gap-2'>
                  <Icon
                    name='not-allowed-black'
                    folder='core'
                    height={20}
                    width={20}
                  />
                  <p className='text-xs'>
                    Access Denied: Decryption Key Invalid.
                  </p>
                </div>
              ) : null}

              {passwordErrors.code ? (
                <p className='text-xs'>{passwordErrors.code.message}</p>
              ) : null}

              <button
                type='submit'
                className='bg-mint-700 font-fredoka mt-2 h-10 w-full rounded-full border-2 text-lg font-bold text-black disabled:cursor-not-allowed'
                disabled={loading}
              >
                Proceed
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
